import { useState } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, StyleSheet,
  ActivityIndicator, TextInput,
} from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useAuth } from '@/hooks/useAuth';
import { useRetention } from '@/hooks/useRetention';
import { useRecoveryProof } from '@/hooks/useRecoveryProof';
import { trackEvent } from '@/lib/analytics';
import {
  RetentionCategory, CATEGORY_LABEL, CATEGORY_COLOR, CATEGORY_ORDER,
} from '@/lib/retention';
import { NotifyModal } from '@/app/(personal)/retencao/_components/NotifyModal';
import { RetentionTrendChart } from '@/app/(personal)/retencao/_components/RetentionTrendChart';
import { RetentionTour } from '@/app/(personal)/retencao/_components/RetentionTour';

type Filter = RetentionCategory | 'todos';

export function RetentionDesktop() {
  const { profile } = useAuth();
  const router = useRouter();
  const [filter, setFilter] = useState<Filter>('todos');
  const [search, setSearch] = useState('');
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);

  const { data, isLoading } = useRetention(profile?.id ?? '');
  const { data: recovery } = useRecoveryProof(profile?.id ?? '');

  if (isLoading || !data) {
    return <ActivityIndicator style={{ flex: 1 }} color="#C8102E" />;
  }

  const { kpis, counts, total, students } = data;

  const filteredStudents = students.filter(s => {
    if (filter !== 'todos' && s.category !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      const name = (s.full_name ?? s.email ?? '').toLowerCase();
      if (!name.includes(q)) return false;
    }
    return true;
  });

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>

      {/* Header */}
      <View style={styles.header}>
        <View>
          <Text style={styles.title}>Área de Retenção</Text>
          <Text style={styles.subtitle}>{kpis.activeStudents} alunos ativos · {kpis.engagementRate}% engajamento</Text>
        </View>
        <View style={styles.headerActions}>
          <TouchableOpacity onPress={() => setTourOpen(true)} style={styles.tourBtn} activeOpacity={0.7}>
            <Ionicons name="help-circle-outline" size={14} color="#C8102E" />
            <Text style={styles.tourBtnText}>Ver tour</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.notifyBtn}
            onPress={() => {
              trackEvent('pt_clicked_notify', { filter, count: filteredStudents.length, surface: 'desktop' });
              setNotifyOpen(true);
            }}
            disabled={filteredStudents.length === 0}
            activeOpacity={0.7}
          >
            <Ionicons name="paper-plane-outline" size={14} color="#fff" />
            <Text style={styles.notifyBtnText}>Notificar em lote</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* KPIs */}
      <View style={styles.kpiRow}>
        <Kpi label="Alunos ativos" value={String(kpis.activeStudents)} />
        <Kpi label="Engajamento" value={String(kpis.engagementRate)} unit="%" />
        <Kpi label="Crescimento" value={String(kpis.growthRate)} unit="%" highlight={kpis.growthRate > 0} />
        <Kpi label="Recuperação" value={String(kpis.recoveryRate)} unit="%" highlight={kpis.recoveryRate > 0} />
      </View>

      {/* Resultado dos seus toques (loop fechado) */}
      <View style={[styles.card, { marginBottom: 16 }]}>
        <Text style={styles.cardTitle}>Resultado dos seus toques</Text>
        {recovery && recovery.notificados > 0 ? (
          <>
            <Text style={styles.cardSub}>Últimos {recovery.janelaDias} dias</Text>
            <View style={styles.recoveryRow}>
              <Text style={styles.recoveryBig}>{recovery.voltaram}</Text>
              <Text style={styles.recoveryOf}>
                de {recovery.notificados} alunos que você notificou voltaram a treinar 🎉
              </Text>
            </View>
            <View style={styles.recoveryTrack}>
              <View style={[styles.recoveryFill, { width: `${recovery.taxa}%` }]} />
            </View>
          </>
        ) : (
          <Text style={styles.recoveryEmpty}>
            Quando você notificar alunos que sumiram, eu mostro aqui quantos voltaram a treinar depois do seu toque.
          </Text>
        )}
      </View>

      {/* 2 colunas */}
      <View style={styles.twoCol}>

        {/* Esquerda: barras + tendência */}
        <View style={styles.leftCol}>
          <View style={styles.card}>
            <Text style={styles.cardTitle}>Engajamento dos alunos</Text>
            <Text style={styles.cardSub}>Classificação baseada na última semana</Text>
            {CATEGORY_ORDER.map(cat => {
              const count = counts[cat];
              const pct = total > 0 ? Math.round((count / total) * 100) : 0;
              return (
                <View key={cat} style={styles.barRow}>
                  <View style={styles.barLabelRow}>
                    <Text style={styles.barLabel}>{CATEGORY_LABEL[cat]}</Text>
                    <Text style={styles.barValue}>{count} ({pct}%)</Text>
                  </View>
                  <View style={styles.barTrack}>
                    <View style={[styles.barFill, { width: `${pct}%`, backgroundColor: CATEGORY_COLOR[cat] }]} />
                  </View>
                </View>
              );
            })}
          </View>

          <View style={styles.card}>
            <Text style={styles.cardTitle}>Tendência das últimas semanas</Text>
            <Text style={styles.cardSub}>Distribuição de categorias por semana</Text>
            <RetentionTrendChart personalId={profile?.id ?? ''} />
          </View>
        </View>

        {/* Direita: tabela de alunos */}
        <View style={styles.rightCol}>
          <View style={styles.card}>
            <Text style={styles.cardTitle}>Alunos</Text>

            <View style={styles.toolbar}>
              <View style={styles.searchBox}>
                <Ionicons name="search-outline" size={14} color="#888" />
                <TextInput
                  style={styles.searchInput}
                  placeholder="Buscar por nome ou email…"
                  placeholderTextColor="#aaa"
                  value={search}
                  onChangeText={setSearch}
                />
              </View>
            </View>

            <View style={styles.filterChips}>
              <Chip label={`Todos (${total})`} active={filter === 'todos'} onPress={() => setFilter('todos')} />
              {CATEGORY_ORDER.map(cat => (
                <Chip
                  key={cat}
                  label={`${CATEGORY_LABEL[cat]} (${counts[cat]})`}
                  active={filter === cat}
                  color={CATEGORY_COLOR[cat]}
                  onPress={() => setFilter(cat)}
                />
              ))}
            </View>

            {/* Tabela */}
            <View style={styles.table}>
              <View style={styles.thead}>
                <Text style={[styles.th, { flex: 3 }]}>Aluno</Text>
                <Text style={[styles.th, { flex: 2 }]}>Última atividade</Text>
                <Text style={[styles.th, { flex: 1.5, textAlign: 'right' }]}>Categoria</Text>
                <Text style={[styles.th, { flex: 0.5, textAlign: 'right' }]}></Text>
              </View>

              {filteredStudents.length === 0 ? (
                <Text style={styles.empty}>
                  {students.length === 0
                    ? 'Nenhum aluno cadastrado ainda.'
                    : 'Nenhum aluno nessa categoria com esse filtro.'}
                </Text>
              ) : (
                filteredStudents.map((s, idx) => {
                  const initials = (s.full_name ?? s.email ?? '?').charAt(0).toUpperCase();
                  const meta = s.daysSinceLast === null
                    ? 'nunca treinou'
                    : s.daysSinceLast === 0
                    ? 'treinou hoje'
                    : `${s.daysSinceLast} dia${s.daysSinceLast === 1 ? '' : 's'} sem treinar`;
                  return (
                    <TouchableOpacity
                      key={s.id}
                      style={[styles.tr, idx % 2 === 1 && { backgroundColor: '#FAFAFA' }]}
                      onPress={() => router.push(`/(personal)/students/${s.id}` as never)}
                      activeOpacity={0.7}
                    >
                      <View style={[styles.td, { flex: 3, flexDirection: 'row', alignItems: 'center', gap: 10 }]}>
                        <View style={styles.avatar}>
                          <Text style={styles.avatarText}>{initials}</Text>
                        </View>
                        <View style={{ flex: 1, minWidth: 0 }}>
                          <Text style={styles.cellName} numberOfLines={1}>{s.full_name ?? s.email}</Text>
                          {s.full_name && <Text style={styles.cellSub} numberOfLines={1}>{s.email}</Text>}
                        </View>
                      </View>

                      <View style={[styles.td, { flex: 2 }]}>
                        <Text style={styles.cellText} numberOfLines={1}>{meta}</Text>
                        {s.sessionsLastWeek > 0 && (
                          <Text style={styles.cellSub}>{s.sessionsLastWeek} essa semana</Text>
                        )}
                      </View>

                      <View style={[styles.td, { flex: 1.5, alignItems: 'flex-end' }]}>
                        <View style={[styles.categoryBadge, { backgroundColor: CATEGORY_COLOR[s.category] + '22' }]}>
                          <Text style={[styles.categoryBadgeText, { color: CATEGORY_COLOR[s.category] }]}>
                            {CATEGORY_LABEL[s.category]}
                          </Text>
                        </View>
                      </View>

                      <View style={[styles.td, { flex: 0.5, alignItems: 'flex-end' }]}>
                        <Ionicons name="chevron-forward" size={16} color="#aaa" />
                      </View>
                    </TouchableOpacity>
                  );
                })
              )}
            </View>
          </View>
        </View>
      </View>

      {notifyOpen && (
        <NotifyModal
          visible={notifyOpen}
          onClose={() => setNotifyOpen(false)}
          personalId={profile?.id ?? ''}
          preselected={filteredStudents}
          initialCategory={filter}
        />
      )}

      <RetentionTour forceOpen={tourOpen} onFinish={() => setTourOpen(false)} />
    </ScrollView>
  );
}

// ─── Sub-componentes ────────────────────────────────────────────────────────

function Kpi({
  label, value, unit, highlight = false,
}: { label: string; value: string; unit?: string; highlight?: boolean }) {
  return (
    <View style={styles.kpi}>
      <Text style={styles.kpiLabel}>{label}</Text>
      <Text style={[styles.kpiValue, highlight && styles.kpiValueHighlight]}>
        {value}{unit && <Text style={styles.kpiUnit}>{unit}</Text>}
      </Text>
    </View>
  );
}

function Chip({
  label, active, color, onPress,
}: { label: string; active: boolean; color?: string; onPress: () => void }) {
  return (
    <TouchableOpacity
      style={[styles.chip, active && { backgroundColor: color ?? '#C8102E' }]}
      onPress={onPress}
      activeOpacity={0.7}
    >
      <Text style={[styles.chipText, active && { color: '#fff' }]}>{label}</Text>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F5F5F5' },
  content: { padding: 24, paddingBottom: 48 },

  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 },
  title: { fontSize: 22, fontWeight: '800', color: '#1A1A1A' },
  subtitle: { fontSize: 13, color: '#888', marginTop: 4 },
  headerActions: { flexDirection: 'row', alignItems: 'center', gap: 10 },

  tourBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    paddingHorizontal: 12, paddingVertical: 8, borderRadius: 8,
    backgroundColor: '#FDEDEF',
  },
  tourBtnText: { color: '#C8102E', fontSize: 12, fontWeight: '600' },

  notifyBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: '#C8102E', paddingHorizontal: 14, paddingVertical: 9, borderRadius: 8,
  },
  notifyBtnText: { color: '#fff', fontSize: 13, fontWeight: '700' },

  kpiRow: { flexDirection: 'row', gap: 12, marginBottom: 16 },
  kpi: {
    flex: 1, backgroundColor: '#fff', borderRadius: 10, padding: 14,
    borderWidth: 1, borderColor: '#EEE',
  },
  kpiLabel: { fontSize: 11, color: '#888', fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 },
  kpiValue: { fontSize: 24, fontWeight: '800', color: '#1A1A1A' },
  kpiValueHighlight: { color: '#2E7D32' },
  kpiUnit: { fontSize: 14, fontWeight: '500', color: '#888' },

  twoCol: { flexDirection: 'row', gap: 16, alignItems: 'flex-start' },
  leftCol: { width: 360, gap: 12 },
  rightCol: { flex: 1, gap: 12 },

  card: { backgroundColor: '#fff', borderRadius: 10, padding: 16, borderWidth: 1, borderColor: '#EEE' },
  cardTitle: { fontSize: 14, fontWeight: '700', color: '#1A1A1A' },
  cardSub: { fontSize: 11, color: '#888', marginTop: 2, marginBottom: 12 },

  barRow: { marginBottom: 10 },
  barLabelRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 },
  barLabel: { fontSize: 12, color: '#1A1A1A', fontWeight: '600' },
  barValue: { fontSize: 11, color: '#555' },
  barTrack: { height: 6, backgroundColor: '#EEE', borderRadius: 3, overflow: 'hidden' },
  barFill: { height: 6, borderRadius: 3 },

  toolbar: { flexDirection: 'row', alignItems: 'center', gap: 12, marginTop: 8, marginBottom: 10 },
  searchBox: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: '#fff', borderWidth: 1, borderColor: '#EEE',
    paddingHorizontal: 12, paddingVertical: 8, borderRadius: 8,
    flex: 1,
  },
  searchInput: { flex: 1, fontSize: 13, color: '#1A1A1A', outlineStyle: 'none' as never },

  filterChips: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 12 },
  chip: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16, backgroundColor: '#fff', borderWidth: 1, borderColor: '#EEE' },
  chipText: { fontSize: 12, color: '#555', fontWeight: '600' },

  table: { borderRadius: 8, overflow: 'hidden', borderWidth: 1, borderColor: '#EEE' },
  thead: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    paddingHorizontal: 12, paddingVertical: 10,
    backgroundColor: '#FAFAFA', borderBottomWidth: 1, borderBottomColor: '#EEE',
  },
  th: { fontSize: 11, fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 },

  tr: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    paddingHorizontal: 12, paddingVertical: 10,
    borderBottomWidth: 1, borderBottomColor: '#F2F2F2',
  },
  td: {},

  avatar: {
    width: 32, height: 32, borderRadius: 16,
    backgroundColor: '#C8102E22',
    alignItems: 'center', justifyContent: 'center',
  },
  avatarText: { fontSize: 12, fontWeight: '700', color: '#C8102E' },

  cellName: { fontSize: 13, fontWeight: '600', color: '#1A1A1A' },
  cellSub: { fontSize: 11, color: '#888', marginTop: 2 },
  cellText: { fontSize: 13, color: '#555' },

  categoryBadge: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 12 },
  categoryBadgeText: { fontSize: 11, fontWeight: '700' },

  empty: { fontSize: 13, color: '#888', textAlign: 'center', padding: 40 },

  recoveryRow: { flexDirection: 'row', alignItems: 'baseline', gap: 10, marginTop: 8 },
  recoveryBig: { fontSize: 30, fontWeight: '800', color: '#2E7D32' },
  recoveryOf: { fontSize: 14, color: '#1A1A1A', fontWeight: '600', flex: 1 },
  recoveryTrack: { height: 8, backgroundColor: '#EEE', borderRadius: 4, overflow: 'hidden', marginTop: 12 },
  recoveryFill: { height: 8, borderRadius: 4, backgroundColor: '#2E7D32' },
  recoveryEmpty: { fontSize: 13, color: '#888', marginTop: 8, lineHeight: 19 },
});
